/**
 * ResetPasswordPage Component
 * Handles both password reset request and actual password reset
 * - Without token: Shows email input form to request reset
 * - With token: Shows password input form to set new password
 */

import { useState, useCallback, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { authService } from '../services/auth.service';
import { useAuthStore } from '../stores/auth.store';
import { Input } from '../components/ui/Input';
import { Button } from '../components/ui/Button';
import { Turnstile } from '../components/ui/Turnstile';
import { AuthenticatedSessionWarning } from '../components/auth/AuthenticatedSessionWarning';
import { useToast } from '../hooks/useToast';
import { useTurnstile } from '../hooks/useTurnstile';
import { validatePassword, getPasswordHelperText } from '../lib/passwordValidation';
import { validateEmail } from '../lib/emailValidation';
import { handleApiError } from '../lib/axios';

export const ResetPasswordPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const toast = useToast();

  const token = searchParams.get('token');
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const { turnstileToken, onVerify, resetTurnstile, isTurnstileReady } = useTurnstile();

  // Request form state
  const [email, setEmail] = useState('');
  const [emailError, setEmailError] = useState<string | null>(null);
  const [requestSent, setRequestSent] = useState(false);

  // Reset form state
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [resetSuccess, setResetSuccess] = useState(false);

  // Token validation state
  const [tokenValid, setTokenValid] = useState<boolean | null>(null); // null = loading
  const [tokenError, setTokenError] = useState<string | null>(null);

  // Validate token on page load
  useEffect(() => {
    if (token) {
      authService.validateResetToken(token).then((result) => {
        setTokenValid(result.valid);
        if (!result.valid) {
          setTokenError(result.error || 'Invalid or expired reset token');
        }
      });
    }
  }, [token]);

  // Request password reset mutation
  const requestMutation = useMutation({
    mutationFn: (email: string) => authService.requestPasswordReset(email, turnstileToken),
    onSuccess: () => {
      setRequestSent(true);
    },
    onError: () => {
      resetTurnstile();
      // Still show success message to prevent email enumeration
      setRequestSent(true);
    },
  });

  // Reset password mutation
  const resetMutation = useMutation({
    mutationFn: (data: { token: string; password: string }) =>
      authService.resetPassword(data.token, data.password),
    onSuccess: () => {
      setResetSuccess(true);
      toast.success('Password reset successfully!');
    },
    onError: (error: unknown) => {
      toast.error(handleApiError(error));
    },
  });

  // Email validation on blur
  const handleEmailBlur = useCallback(() => {
    if (!email.trim()) {
      setEmailError(null);
      return;
    }
    const result = validateEmail(email);
    setEmailError(result.error);
  }, [email]);

  // Clear email error when user types
  const handleEmailChange = (value: string) => {
    setEmail(value);
    if (emailError) {
      setEmailError(null);
    }
  };

  // Handle request form submit
  const handleRequestSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!email) {
      toast.error('Please enter your email address');
      return;
    }

    // Validate email format
    const emailResult = validateEmail(email);
    if (!emailResult.isValid) {
      setEmailError(emailResult.error);
      return;
    }

    requestMutation.mutate(email);
  };

  // Handle reset form submit
  const handleResetSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setPasswordError('');

    const validation = validatePassword(password);
    if (!validation.valid) {
      setPasswordError(validation.error!);
      return;
    }

    if (password !== confirmPassword) {
      setPasswordError('Passwords do not match');
      return;
    }

    if (!token) {
      toast.error('Invalid reset token');
      return;
    }

    resetMutation.mutate({ token, password });
  };

  // Success state after reset
  if (resetSuccess) {
    return (
      <div className="min-h-screen bg-surface-app flex flex-col justify-center py-12 sm:px-6 lg:px-8">
        <div className="sm:mx-auto sm:w-full sm:max-w-md">
          <img
            src="/team-wizard-logo-small.png"
            alt="Team Wizard"
            className="mx-auto h-16 w-auto"
          />
          <div className="mt-8 bg-surface py-8 px-4 shadow-lg sm:rounded-lg sm:px-10">
            <div className="text-center">
              <svg
                className="mx-auto h-12 w-12 text-green-500 mb-4"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              <h2 className="text-2xl font-bold text-heading mb-2">Password Reset</h2>
              <p className="text-meta mb-6">
                Your password has been reset successfully. You can now log in with your new password.
              </p>
              <Button onClick={() => navigate('/login')} className="w-full">
                Go to Login
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Request sent confirmation
  if (requestSent) {
    return (
      <div className="min-h-screen bg-surface-app flex flex-col justify-center py-12 sm:px-6 lg:px-8">
        <div className="sm:mx-auto sm:w-full sm:max-w-md">
          <img
            src="/team-wizard-logo-small.png"
            alt="Team Wizard"
            className="mx-auto h-16 w-auto"
          />
          <div className="mt-8 bg-surface py-8 px-4 shadow-lg sm:rounded-lg sm:px-10">
            <div className="text-center">
              <svg
                className="mx-auto h-12 w-12 text-blue-500 mb-4"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                />
              </svg>
              <h2 className="text-2xl font-bold text-heading mb-2">Check Your Email</h2>
              <p className="text-meta mb-6">
                If an account exists, you will receive an email with reset instructions.
              </p>
              <Button variant="secondary" onClick={() => navigate('/login')}>
                Back to Login
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Token validation error screen
  if (token && tokenValid === false) {
    return (
      <div className="min-h-screen bg-surface-app flex flex-col justify-center py-12 sm:px-6 lg:px-8">
        <div className="sm:mx-auto sm:w-full sm:max-w-md">
          <img
            src="/team-wizard-logo-small.png"
            alt="Team Wizard"
            className="mx-auto h-16 w-auto"
          />
          <div className="mt-8 bg-surface py-8 px-4 shadow-lg sm:rounded-lg sm:px-10">
            <div className="text-center">
              <svg
                className="mx-auto h-12 w-12 text-red-500 mb-4"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                />
              </svg>
              <h2 className="text-2xl font-bold text-heading mb-2">Link Expired</h2>
              <p className="text-meta mb-6">{tokenError}</p>
              <Button onClick={() => navigate('/reset-password')} className="w-full">
                Request New Reset Link
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Loading state while validating token
  if (token && tokenValid === null) {
    return (
      <div className="min-h-screen bg-surface-app flex flex-col justify-center py-12 sm:px-6 lg:px-8">
        <div className="sm:mx-auto sm:w-full sm:max-w-md text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-meta">Validating reset link...</p>
        </div>
      </div>
    );
  }

  // Reset form (with token)
  if (token) {
    return (
      <div className="min-h-screen bg-surface-app flex flex-col justify-center py-12 sm:px-6 lg:px-8">
        <div className="sm:mx-auto sm:w-full sm:max-w-md">
          <img
            src="/team-wizard-logo-small.png"
            alt="Team Wizard"
            className="mx-auto h-16 w-auto"
          />
          <h2 className="mt-6 text-center text-3xl font-bold text-heading">
            Set New Password
          </h2>
          <p className="mt-2 text-center text-sm text-meta">
            Enter your new password below
          </p>
        </div>

        <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
          <div className="bg-surface py-8 px-4 shadow-lg sm:rounded-lg sm:px-10">
            {isAuthenticated && (
              <AuthenticatedSessionWarning reason="reset this password" />
            )}
            <form onSubmit={handleResetSubmit}>
              {/* fieldset[disabled] truly inerts every control (removes tab
                  order + blocks submit) for an already-signed-in user, unlike
                  pointer-events which the keyboard bypasses. TW-168 finding #3. */}
              <fieldset
                disabled={isAuthenticated}
                className={`space-y-6 border-0 p-0 m-0 ${isAuthenticated ? 'opacity-50' : ''}`}
              >
                <Input
                  label="New Password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter new password"
                  required
                  helperText={getPasswordHelperText()}
                />

                <Input
                  label="Confirm Password"
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Confirm new password"
                  required
                />

                {passwordError && (
                  <p className="text-sm text-red-600 dark:text-red-400">{passwordError}</p>
                )}

                <Button
                  type="submit"
                  className="w-full"
                  isLoading={resetMutation.isPending}
                >
                  Reset Password
                </Button>
              </fieldset>
            </form>
          </div>
        </div>
      </div>
    );
  }

  // Request form (no token)
  return (
    <div className="min-h-screen bg-surface-app flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <img
          src="/team-wizard-logo-small.png"
          alt="Team Wizard"
          className="mx-auto h-16 w-auto"
        />
        <h2 className="mt-6 text-center text-3xl font-bold text-heading">
          Reset Password
        </h2>
        <p className="mt-2 text-center text-sm text-meta">
          Enter your email address and we'll send you a reset link
        </p>
      </div>

      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
        <div className="bg-surface py-8 px-4 shadow-lg sm:rounded-lg sm:px-10">
          <form onSubmit={handleRequestSubmit} className="space-y-6">
            <Input
              label="Email Address"
              type="email"
              value={email}
              onChange={(e) => handleEmailChange(e.target.value)}
              onBlur={handleEmailBlur}
              placeholder="Enter your email"
              required
              error={emailError || undefined}
            />

            <Turnstile onVerify={onVerify} action="reset-password" />

            <Button
              type="submit"
              className="w-full"
              isLoading={requestMutation.isPending}
              disabled={!isTurnstileReady}
            >
              Send Reset Link
            </Button>

            <div className="text-center">
              <button
                type="button"
                onClick={() => navigate('/login')}
                className="text-sm text-blue-600 dark:text-blue-400 hover:text-blue-500 dark:hover:text-blue-300"
              >
                Back to Login
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};
